import { Flex } from '@radix-ui/themes';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useEditBoardSettings from './useEditBoardSettings';
import NavigationGuard from '../../../shared/NavigationGuard';
import Tooltip from '../../../shared/Tooltip/Tooltip.jsx';
import Email from './EmailSection/Email.jsx';
import UnsavedChangesModal from '../../../shared/UnsavedChangesModal';
import { Button as FormButton } from '../../../shared/FormStyles';
import SectionCard from '../../../shared/SectionCard/SectionCard.jsx';
import TextInput from '../../../shared/FormStyles/TextInput.jsx';
import ToggleSwitch from '../../../shared/FormStyles/ToggleSwitch.jsx';
import Select from '../../../shared/Select';
import { useGetTimezones } from '../data';
import { Label, Information } from './StyledComponents';
import styles from '../BoardSetup.module.css';

const UnsubscribeWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const UnsubscribeLabel = styled(Label)`
  margin: 0;
`;

const ToggleDiv = styled.div`
  margin-left: auto;
`;

const Notifications = () => {
  const {
    handleChange,
    values,
    initialValues,
    handleSubmit,
    setFieldValue,
    inputError,
    handleBlur,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    setPendingAction,
    handleCloseModal,
    handleDiscardChanges,
    handleSaveAndLeave,
  } = useEditBoardSettings();
  const { t } = useTranslation(['shared', 'boardSetup']);
  const timezoneOptions = useGetTimezones();

  const isEmailSettingsDirty =
    values.email !== initialValues.email ||
    values.customReplyEmailName !== initialValues.customReplyEmailName ||
    values.allowUnsubscribes !== initialValues.allowUnsubscribes ||
    values.timezone !== initialValues.timezone;

  const isInvitationDirty =
    values.customInviteEmailSubject !==
      initialValues.customInviteEmailSubject ||
    values.persistentMessage !== initialValues.persistentMessage;

  const isDirty = isEmailSettingsDirty || isInvitationDirty;

  const resetEmailSettingsForm = () => {
    setFieldValue('email', initialValues.email);
    setFieldValue('customReplyEmailName', initialValues.customReplyEmailName);
    setFieldValue('allowUnsubscribes', initialValues.allowUnsubscribes);
    setFieldValue('timezone', initialValues.timezone);
  };

  const resetInvitationForm = () => {
    setFieldValue(
      'customInviteEmailSubject',
      initialValues.customInviteEmailSubject,
    );
    setFieldValue('persistentMessage', initialValues.persistentMessage);
  };

  return (
    <NavigationGuard
      hasUnsavedChanges={isDirty}
      showUnsavedChangesModal={showUnsavedChangesModal}
      setShowUnsavedChangesModal={setShowUnsavedChangesModal}
      setPendingAction={setPendingAction}
    >
      <Tooltip />
      <Flex direction="row" gap="4">
        <SectionCard
          className={styles.sectionCardContainer}
          headerClassName={styles.sectionCardHeader}
          title={t('boardSetup:email.title')}
        >
          <Flex direction="column" gap="4" py="4" height="100%">
            <Email
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              demo={values}
              inputError={inputError}
              handleBlur={handleBlur}
            />
            <UnsubscribeWrapper>
              <UnsubscribeLabel>
                {t('boardSetup:additionalSettings.allowUnsubscribes')}
              </UnsubscribeLabel>
              <Information
                className="fa-regular fa-circle-info fa-xs"
                data-tip={t(
                  'boardSetup:additionalSettings.allowUnsubscribesDataTip',
                )}
                data-offset="{ 'right': 125 }"
              />
              <ToggleDiv>
                <ToggleSwitch
                  onChange={handleChange}
                  value={values.allowUnsubscribes}
                  label="Allow"
                  name="allowUnsubscribes"
                  id="allow-unsubscribe"
                />
              </ToggleDiv>
            </UnsubscribeWrapper>
            <Select
              onChange={option => setFieldValue('timezone', option.value)}
              label={t('boardSetup:additionalSettings.label')}
              options={timezoneOptions}
              value={{ value: values.timezone, label: values.timezone }}
              isSearchable={false}
              name="timezone"
              placeholder={t('boardSetup:additionalSettings.placeholder')}
              menuPortalTarget={document.body}
              components={{
                IndicatorSeparator: () => null,
              }}
            />
            <Flex justify="end" gap="3" mt="auto">
              <FormButton variant="tertiary" onClick={resetEmailSettingsForm}>
                {t('shared:cancel')}
              </FormButton>
              <FormButton
                ml="8px"
                variant="containedBlue"
                disabled={!isEmailSettingsDirty}
                onClick={handleSubmit}
                type="submit"
              >
                {t('shared:save')}
              </FormButton>
            </Flex>
          </Flex>
        </SectionCard>
        <SectionCard
          className={styles.sectionCardContainer}
          headerClassName={styles.sectionCardHeader}
          title={t('boardSetup:newUserInvitation.title')}
        >
          <Flex direction="column" gap="4" py="4" height="100%">
            <TextInput
              label={t('boardSetup:customInviteSubject.label')}
              dataTip={t('boardSetup:customInviteSubject.dataTip')}
              dataOffset="{ 'right': 150 }"
              name="customInviteEmailSubject"
              value={values.customInviteEmailSubject}
              onChange={handleChange}
              placeholder={t('boardSetup:customInviteSubject.placeholder')}
            />
            <TextInput
              label={t('boardSetup:welcomeBanner.labelNewAirbo')}
              dataTip={t('boardSetup:welcomeBanner.labelNewAirboDataTip')}
              dataOffset="{ 'right': 150 }"
              charactersLimitVariant="small1"
              as="textarea"
              charactersLimit={400}
              name="persistentMessage"
              value={values.persistentMessage}
              onChange={handleChange}
            />
            <Flex justify="end" gap="3" mt="auto">
              <FormButton variant="tertiary" onClick={resetInvitationForm}>
                {t('shared:cancel')}
              </FormButton>
              <FormButton
                ml="8px"
                variant="containedBlue"
                disabled={!isInvitationDirty}
                onClick={handleSubmit}
                type="submit"
              >
                {t('shared:save')}
              </FormButton>
            </Flex>
          </Flex>
        </SectionCard>
      </Flex>
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onClose={handleCloseModal}
        onDiscard={handleDiscardChanges}
        onSave={handleSaveAndLeave}
      />
    </NavigationGuard>
  );
};

export default Notifications;
